/**
 * Platform-agnostic local persistence boundary for synchronized data.
 *
 * Adapters own the storage engine and implement this contract. Domain and
 * sync-core code must depend on these operations rather than on browser or
 * mobile database APIs.
 *
 * @typedef {Object} LocalTaskRecord
 * @property {string} id
 * @property {string} definition
 * @property {string|null} completed_at
 * @property {string} [created_at]
 * @property {string} [updated_at]
 */

/**
 * @typedef {Object} LocalWorkIntervalRecord
 * @property {string} id
 * @property {string|null} task_id
 * @property {boolean} completion_confirmed
 * @property {string} started_at
 * @property {string} ended_at
 * @property {string} [created_at]
 * @property {string} [updated_at]
 */

/**
 * @typedef {Object} LocalSyncRecord
 * @property {'task'|'work_interval'} entity_type
 * @property {string} entity_id
 * @property {number} server_version
 * @property {string|null} deleted_at
 */

/**
 * @typedef {Object} SyncCursor
 * @property {number} last_seen_server_version
 */

/**
 * @typedef {Object} LocalAuthoritativeChange
 * @property {number} server_version
 * @property {'task'|'work_interval'} entity_type
 * @property {string} entity_id
 * @property {'create'|'update'|'delete'} op
 * @property {LocalTaskRecord|LocalWorkIntervalRecord|null} payload
 */

/**
 * @typedef {Object} LocalDatabase
 * @property {(options: {ownerId: string}) => Promise<void>} open
 *
 * `open` binds the adapter to one authenticated owner. Every subsequent
 * operation must read and write only that owner's local state.
 *
 * @property {() => Promise<void>} close
 * @property {(work: (database: LocalDatabase) => Promise<unknown>) => Promise<unknown>} transaction
 *
 * A transaction commits when the callback resolves and restores all domain,
 * registry, and cursor state when the callback rejects.
 *
 * @property {(entityType: 'task'|'work_interval', id: string) => Promise<(LocalTaskRecord|LocalWorkIntervalRecord)|null>} getEntity
 * @property {(entityType: 'task'|'work_interval') => Promise<(LocalTaskRecord|LocalWorkIntervalRecord)[]>} listEntities
 * @property {(entityType: 'task'|'work_interval', ids: string[]) => Promise<(LocalTaskRecord|LocalWorkIntervalRecord)[]>} listEntitiesByIds
 * @property {(entityType: 'task'|'work_interval', entity: LocalTaskRecord|LocalWorkIntervalRecord) => Promise<void>} upsertEntity
 * @property {(entityType: 'task'|'work_interval', id: string) => Promise<void>} deleteEntity
 * @property {(entityType: 'task'|'work_interval', entityId: string) => Promise<LocalSyncRecord|null>} getSyncRecord
 * @property {(record: LocalSyncRecord) => Promise<void>} upsertSyncRecord
 * @property {() => Promise<SyncCursor>} getSyncCursor
 * @property {(serverVersion: number) => Promise<void>} setSyncCursor
 * @property {() => Promise<void>} clearSyncState
 *
 * `clearSyncState` clears the currently opened owner's synchronized domain
 * rows, registry rows, and cursor, resetting the cursor to zero atomically.
 */

export const LOCAL_DATABASE_METHODS = Object.freeze([
  'open',
  'close',
  'transaction',
  'getEntity',
  'listEntities',
  'listEntitiesByIds',
  'upsertEntity',
  'deleteEntity',
  'getSyncRecord',
  'upsertSyncRecord',
  'getSyncCursor',
  'setSyncCursor',
  'clearSyncState'
]);

/**
 * Validates the structural contract at an adapter boundary.
 *
 * This is intentionally limited to method presence. Entity validation and
 * transaction behavior belong to the compliance suite and the adapter's
 * storage-specific implementation.
 *
 * @param {unknown} database - Candidate local database adapter.
 * @returns {LocalDatabase} The same adapter after validation.
 * @throws {TypeError} When a required operation is missing.
 */
export const assertLocalDatabase = (database) => {
  if (!database || typeof database !== 'object') {
    throw new TypeError('LocalDatabase adapter must be an object');
  }

  const missingMethod = LOCAL_DATABASE_METHODS.find(
    (method) => typeof database[method] !== 'function'
  );
  if (missingMethod) {
    throw new TypeError(
      `LocalDatabase adapter is missing the ${missingMethod} operation`
    );
  }

  return database;
};
